/**
 * Reports how long a visitor stayed on a public page, and how far down it they
 * got. Nothing else.
 *
 * The rule this is built to, and the reason there is no cookie banner on this
 * site: it stores nothing on the visitor's device. No cookie, no localStorage,
 * no sessionStorage, no IndexedDB. UK PECR regulation 6 — the one that puts
 * banners on websites — governs *storing or accessing information on terminal
 * equipment*, and this does neither. The token that ties "arrived" to "left" is
 * a JavaScript variable that dies with the page; a variable is not storage.
 *
 * It also does no fingerprinting. No canvas, no font enumeration, no screen or
 * hardware inventory — the ICO treats fingerprinting as access to the device,
 * which would drag this straight back into scope. The user agent that the
 * server reads is one the browser volunteers on every request; we never reach
 * for it here.
 *
 * The server has already counted the page view by the time this runs. If it
 * never fires — blocked, crashed, browser killed — the visit is still counted,
 * just without a duration. Nothing depends on it.
 */

const ENDPOINT = '/pulse'

// The page currently being measured. Module-level because Turbo swaps bodies
// without ever firing pagehide, so leaving one page for another in-app is a
// thing this has to notice for itself — see initAnalyticsBeacon.
let current: { send: () => void } | null = null

function startAnalyticsBeacon(token: string) {
  const startedAt = performance.now()
  let maxScroll = 0
  let sent = false

  function recordScroll() {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight

    // A page shorter than the viewport has nothing to scroll, and everyone who
    // opens it has seen all of it — 100, not 0, which is what dividing by zero
    // would otherwise have to be rounded to.
    const percent =
      scrollable <= 0 ? 100 : Math.round(((window.scrollY + window.innerHeight) / document.documentElement.scrollHeight) * 100)

    if (percent > maxScroll) maxScroll = Math.min(percent, 100)
  }

  function send() {
    if (sent) return
    sent = true

    const body = new URLSearchParams({
      token,
      duration: String(Math.round(performance.now() - startedAt)),
      scroll: String(maxScroll),
    })

    // sendBeacon rather than fetch: the browser takes the request off our hands
    // and delivers it after the page is gone. A fetch fired during pagehide is
    // routinely cancelled mid-flight, which is precisely the moment this needs
    // to work. keepalive fetch is the fallback for anything without it.
    if (navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, body)
    } else {
      fetch(ENDPOINT, { method: 'POST', body, keepalive: true }).catch(() => {})
    }
  }

  recordScroll()
  // passive so scroll handling can never block scrolling itself, which is the
  // one thing a beacon must not cost the page.
  window.addEventListener('scroll', recordScroll, { passive: true })

  return {
    send,
    stop() {
      window.removeEventListener('scroll', recordScroll)
    },
  }
}

/**
 * Wires the beacon up for the life of the session.
 *
 * The token comes from a meta tag the layout only emits for a signed-out
 * visitor on a page that rendered — so in-app pages have no token, and the
 * beacon simply never starts there.
 */
export function initAnalyticsBeacon() {
  function finish() {
    current?.send()
    current = null
  }

  function begin() {
    const token = document.querySelector<HTMLMetaElement>('meta[name="analytics-view-token"]')?.content

    // Belt and braces: a Turbo navigation that lands on a page without a token
    // must not leave the previous page's beacon running and later report its
    // duration against the wrong view.
    finish()
    if (token) current = { ...startAnalyticsBeacon(token) }
  }

  // pagehide rather than unload: unload doesn't fire reliably on mobile Safari
  // and disqualifies the page from the back/forward cache. visibilitychange
  // covers the tab being switched away from and never returned to, which on a
  // phone is how most visits actually end.
  window.addEventListener('pagehide', finish)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') finish()
  })

  // Turbo swaps the body without a page unload, so neither of the above fires
  // when you follow a link. Without this, leaving the landing page for the
  // login form would report nothing at all for the landing page.
  document.addEventListener('turbo:before-visit', finish)
  document.addEventListener('turbo:load', begin)
}
